import { useState, useRef, useEffect } from 'react';
import { Link, Link2Off, Shield, Music, UserPlus, UserCircle, Trash2, Edit2, AlertTriangle, X, Calendar, Plus, Copy, Search, Globe, Mail, ExternalLink, FileText, Lock, MessageSquare, FolderKanban } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { CustomButton } from '../store/useStore';
import { compressImage } from '../lib/imageCompression';
import { v4 as uuidv4 } from 'uuid';
import { ConfigModal } from './ConfigModal';

const ARTIST_COLORS = [
  '#f59e0b', '#10b981', '#6366f1', '#ec4899', '#8b5cf6', '#14b8a6', '#ef4444', '#3b82f6'
];

const normalizeString = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

export function ArtistManagementView() {
  const { team, addTeam, updateTeam, deleteTeam, currentUser, categories, points, allPoints, shifts, addShift, updateShift, deleteShift, currentOrgId, messages, effectiveCategoryId, addMessage } = useVisibleStore();
  
  // Filter for categories that are marked as Artist categories
  const artistCategories = categories.filter(c => c.isArtistCategory);
  const artistCategoryIds = artistCategories.map(c => c.id);
  
  // Visible artists: team with isArtist = true or belonging to an artist category
  const visibleArtists = team.filter(s => 
    s.isArtist && 
    (!effectiveCategoryId || s.categoryId === effectiveCategoryId)
  );

  const defaultCategory = effectiveCategoryId ? effectiveCategoryId : (artistCategoryIds[0] || '');
  
  const defaultArtist = {
    id: undefined as string | undefined,
    name: '',
    categoryId: defaultCategory,
    roles: [] as string[],
    email: '',
    phone: '',
    color: ARTIST_COLORS[0],
    photo: '',
    customButtons: [] as CustomButton[],
    isArtist: true,
    memberCount: 1,
    assignedTeamId: undefined as string | undefined,
  };

  const [formData, setFormData] = useState(defaultArtist);
  const [customRoleInput, setCustomRoleInput] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [initialFormData, setInitialFormData] = useState(defaultArtist);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [linkModal, setLinkModal] = useState<{ show: boolean; artistName: string; link: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterCustomRole, setFilterCustomRole] = useState('');
  const [findArtistsOpen, setFindArtistsOpen] = useState(false);
  const [publicArtists, setPublicArtists] = useState<any[]>([]);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [publicSearch, setPublicSearch] = useState('');
  const [publicGenre, setPublicGenre] = useState('');
  const [isSuperAdminState, setIsSuperAdminState] = useState(false);
  const fileImportRef = useRef<HTMLInputElement>(null);

  // Bulk selection
  const [selectedArtistIds, setSelectedArtistIds] = useState<Set<string>>(new Set());
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState('');

  const toggleArtistSelection = (id: string) => {
    setSelectedArtistIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedArtistIds(new Set());

  const hasSelection = selectedArtistIds.size > 0;
  const handleBulkDelete = () => {
    const count = selectedArtistIds.size;
    if (!window.confirm(`Supprimer définitivement ${count} artiste(s) ?`)) return;
    selectedArtistIds.forEach(id => deleteTeam(id));
    setSelectedArtistIds(new Set());
  };
  const handleBulkMessage = () => {
    const ids = [...selectedArtistIds];
    const content = window.prompt(`Envoyer un message à ${ids.length} artiste(s) :`);
    if (!content?.trim()) return;
    ids.forEach(teamId => addMessage(teamId, content.trim(), currentUser.email || 'Admin'));
    setSelectedArtistIds(new Set());
  };
  const handleBulkReassignCategory = () => {
    if (!bulkCategoryId) return;
    selectedArtistIds.forEach(id => updateTeam(id, { categoryId: bulkCategoryId }));
    setShowBulkCategoryModal(false);
    setBulkCategoryId('');
    setSelectedArtistIds(new Set());
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const emailVal = data.session?.user?.email || data.session?.user?.user_metadata?.email || localStorage.getItem('wfm_user_email');
      setIsSuperAdminState(emailVal?.toLowerCase().trim() === 'benmloukaamen@gmail.com');
    });
  }, []);

  const isSuperAdmin = isSuperAdminState || currentUser?.email === 'benmloukaamen@gmail.com' || (typeof window !== 'undefined' && localStorage.getItem('wfm_user_email') === 'benmloukaamen@gmail.com');

  const loadPublicArtists = async () => {
    setLoadingPublic(true);
    try {
      const { data, error } = await supabase.from('public_artists').select('*').order('name');
      if (error) throw error;
      setPublicArtists(data || []);
    } catch (err: any) {
      console.error("Error loading public artists:", err);
    } finally {
      setLoadingPublic(false);
    }
  };

  const handleAddPublicArtistToLineup = async (artist: any) => {
    const categoryIdToSave = defaultCategory || artistCategoryIds[0];
    if (!categoryIdToSave) {
      alert("Veuillez d'abord créer une catégorie d'artistes dans l'onglet Catégories !");
      return;
    }
    
    // Map CSV social links to Custom Buttons
    const customButtons = [];
    if (artist.spotify) customButtons.push({ label: 'Spotify', url: artist.spotify, color: 'emerald' });
    if (artist.soundcloud) customButtons.push({ label: 'SoundCloud', url: artist.soundcloud, color: 'orange' });
    if (artist.instagram) customButtons.push({ label: 'Instagram', url: artist.instagram, color: 'pink' });
    
    addTeam({
      name: artist.name,
      categoryId: categoryIdToSave,
      roles: artist.category ? [artist.category] : [],
      email: artist.email || '',
      phone: '',
      color: ARTIST_COLORS[Math.floor(Math.random() * ARTIST_COLORS.length)],
      photo: artist.image_url || '',
      customButtons,
      isArtist: true,
      publicDescription: artist.bio || null,
      publicSkills: artist.genres ? artist.genres.split(', ') : []
    });
    
    alert(`L'artiste "${artist.name}" a été ajouté à votre line-up !`);
  };

  const handleDeletePublicArtist = async (id: string, name: string) => {
    if (!confirm(`Supprimer définitivement l'artiste "${name}" de la base globale ?`)) return;
    try {
      const { error } = await supabase.from('public_artists').delete().eq('id', id);
      if (error) throw error;
      setPublicArtists(prev => prev.filter(a => a.id !== id));
      alert(`L'artiste "${name}" a été supprimé.`);
    } catch (err: any) {
      console.error(err);
    }
  };

  function parseCSV(content: string): string[][] {
    const lines: string[][] = [];
    let row = [""];
    let inQuotes = false;
  
    for (let i = 0; i < content.length; i++) {
      const char = content[i];
      const nextChar = content[i + 1];
  
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          row[row.length - 1] += '"';
          i++; // skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        row.push("");
      } else if ((char === '\r' || char === '\n') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') {
          i++; // skip LF
        }
        lines.push(row);
        row = [""];
      } else {
        row[row.length - 1] += char;
      }
    }
    if (row.length > 1 || row[0] !== "") {
      lines.push(row);
    }
    return lines;
  }

  const handleImportCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const text = evt.target?.result as string;
      if (!text) return;
      try {
        const rows = parseCSV(text);
        const headers = rows[0].map(h => h.trim());
        const nameIdx = headers.indexOf('name');
        const imgIdx = headers.indexOf('image');
        const genresIdx = headers.indexOf('genres');
        const categoryIdx = headers.indexOf('Category');
        const spotifyIdx = headers.indexOf('Spotify');
        const soundcloudIdx = headers.indexOf('SoundCloud');
        const instagramIdx = headers.indexOf('Instagram');
        const emailIdx = headers.indexOf('email');
        const bioIdx = headers.indexOf('faq_BIO_0_answer');
  
        if (nameIdx === -1) {
          alert("Le fichier CSV doit contenir une colonne 'name'.");
          return;
        }
  
        const list = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (row.length < 2 || !row[nameIdx]) continue;
          list.push({
            name: row[nameIdx].trim(),
            image_url: imgIdx !== -1 && row[imgIdx] ? row[imgIdx].trim() : '',
            genres: genresIdx !== -1 && row[genresIdx] ? row[genresIdx].trim() : '',
            category: categoryIdx !== -1 && row[categoryIdx] ? row[categoryIdx].trim() : '',
            spotify: spotifyIdx !== -1 && row[spotifyIdx] ? row[spotifyIdx].trim() : '',
            soundcloud: soundcloudIdx !== -1 && row[soundcloudIdx] ? row[soundcloudIdx].trim() : '',
            instagram: instagramIdx !== -1 && row[instagramIdx] ? row[instagramIdx].trim() : '',
            email: emailIdx !== -1 && row[emailIdx] ? row[emailIdx].trim() : '',
            bio: bioIdx !== -1 && row[bioIdx] ? row[bioIdx].trim() : ''
          });
        }
  
        if (list.length === 0) {
          alert("Aucun artiste trouvé dans le CSV.");
          return;
        }
  
        const { error } = await supabase.from('public_artists').insert(list);
        if (error) throw error;
        alert(`${list.length} artistes importés avec succès !`);
        loadPublicArtists();
      } catch (err: any) {
        console.error(err);
        alert("Erreur d'import : " + err.message);
      }
    };
    reader.readAsText(file);
  };

  // ── Scheduling state (balances/performances) ────────────────
  const [schedModalOpen, setSchedModalOpen] = useState(false);
  const [selectedArtistForSched, setSelectedArtistForSched] = useState<any>(null);
  const [editingShiftId, setEditingShiftId] = useState<string | null>(null);
  const [schedForm, setSchedForm] = useState({
    pointId: (allPoints || points)[0]?.id || '',
    start: new Date().toISOString().slice(0, 16),
    end: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
    description: '',
    shiftType: 'performance' as 'soundcheck' | 'performance'
  });

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialFormData);

  // Copy wrapper
  const copyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const generateArtistLink = (s: any) => {
    const token = uuidv4();
    updateTeam(s.id, { teamToken: token });
    const link = `${window.location.origin}/?art_token=${token}`;
    setLinkModal({ show: true, artistName: s.name, link });
  };

  const revokeArtistToken = (s: any) => {
    if (!confirm(`Révoquer l'accès de l'artiste "${s.name}" ? Le lien actuel ne fonctionnera plus.`)) return;
    updateTeam(s.id, { teamToken: undefined });
  };

  const showExistingArtistLink = (s: any) => {
    const link = `${window.location.origin}/?art_token=${s.teamToken}`;
    setLinkModal({ show: true, artistName: s.name, link });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file, 200, 200, 0.7);
      setFormData(prev => ({ ...prev, photo: compressed }));
    } catch (err) {
      console.error('Compression photo échouée:', err);
    }
  };

  const handleSave = () => {
    if (!formData.name) return;
    const categoryIdToSave = effectiveCategoryId ? effectiveCategoryId : formData.categoryId;
    
    if (formData.id) {
      updateTeam(formData.id, { ...formData, categoryId: categoryIdToSave, isArtist: true });
    } else {
      addTeam({ ...formData, categoryId: categoryIdToSave, isArtist: true });
    }
    
    setFormData(defaultArtist);
    setInitialFormData(defaultArtist);
    setModalOpen(false);
  };

  const openNew = () => {
    setFormData(defaultArtist);
    setInitialFormData(defaultArtist);
    setModalOpen(true);
  };

  const openEdit = (artist: any) => {
    if (artist.emailConfirmed && !isSuperAdmin && artist.id !== currentUser.teamId) {
      alert("Cet artiste a un compte vérifié. Seul le superadmin peut modifier ces informations.");
      return;
    }
    const editData = {
      id: artist.id,
      name: artist.name,
      categoryId: artist.categoryId || defaultCategory,
      roles: artist.roles || [],
      email: artist.email || '',
      phone: artist.phone || '',
      color: artist.color || ARTIST_COLORS[0],
      photo: artist.photo || '',
      customButtons: artist.customButtons || [],
      isArtist: true,
      memberCount: artist.memberCount ?? 1,
      assignedTeamId: artist.assignedTeamId || undefined,
    };
    setFormData(editData);
    setInitialFormData(editData);
    setModalOpen(true);
  };

  // ── Scheduling logic ──────────────────────────────────────────
  const openScheduler = (artist: any, existingShift?: any) => {
    setSelectedArtistForSched(artist);
    if (existingShift) {
      setEditingShiftId(existingShift.id);
      setSchedForm({
        pointId: existingShift.pointId || '',
        start: existingShift.start.slice(0, 16),
        end: existingShift.end.slice(0, 16),
        description: existingShift.description || '',
        shiftType: existingShift.shiftType || 'performance'
      });
    } else {
      setEditingShiftId(null);
      setSchedForm({
        pointId: (allPoints || points)[0]?.id || '',
        start: new Date().toISOString().slice(0, 16),
        end: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
        description: '',
        shiftType: 'performance'
      });
    }
    setSchedModalOpen(true);
  };

  const saveSchedule = () => {
    if (!selectedArtistForSched || !schedForm.pointId) return;
    
    const payload = {
      teamId: selectedArtistForSched.id,
      pointId: schedForm.pointId,
      start: schedForm.start,
      end: schedForm.end,
      description: schedForm.description || (schedForm.shiftType === 'soundcheck' ? 'Balance / Soundcheck' : 'Concert / Performance'),
      shiftType: schedForm.shiftType
    };

    if (editingShiftId) {
      updateShift(editingShiftId, payload);
    } else {
      addShift(payload);
    }

    setSchedModalOpen(false);
    setSelectedArtistForSched(null);
    setEditingShiftId(null);
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 h-full flex flex-col overflow-hidden relative">
      <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-amber-500/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Header */}
      <div className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
        <div>
          <h2 className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 bg-clip-text text-transparent flex items-center gap-3">
            <Music className="text-amber-400 w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0 animate-bounce" />
            <span>Gestion des Artistes 🎨</span>
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm mt-2 max-w-2xl leading-relaxed">
            Configurez la liste de vos artistes, gérez leurs quotas VIP de catering, planifiez leurs balances (soundchecks), leurs concerts, et transmettez-leur leurs badges d'accès personnels.
          </p>
        </div>
        
        <button
          onClick={openNew}
          className="flex-shrink-0 flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold text-sm rounded-xl shadow-lg shadow-amber-500/10 transition-all active:scale-95"
        >
          <UserPlus className="w-5 h-5 flex-shrink-0" />
          <span>Ajouter un Artiste</span>
        </button>
      </div>

      {/* Search and Discovery Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3 relative z-10">
        <div className="flex-1 relative">
          <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="Rechercher un artiste par nom ou style..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#14161B]/60 border border-[#2D3139] rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 transition-all"
          />
        </div>
        {/* Category Filter */}
        <select
          value={filterCategoryId}
          onChange={e => setFilterCategoryId(e.target.value)}
          className="bg-[#14161B]/60 border border-[#2D3139] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500 appearance-none"
        >
          <option value="">Toutes catégories</option>
          {artistCategories.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {/* Custom Role Filter */}
        {(() => {
          const distinctRoles = [...new Set(visibleArtists.flatMap(a => a.roles || []).filter(Boolean))].sort();
          if (distinctRoles.length === 0) return null;
          return (
            <select
              value={filterCustomRole}
              onChange={e => setFilterCustomRole(e.target.value)}
              className="bg-[#14161B]/60 border border-[#2D3139] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500 appearance-none"
            >
              <option value="">Tous rôles</option>
              {distinctRoles.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          );
        })()}
        <button
          onClick={() => {
            setFindArtistsOpen(true);
            loadPublicArtists();
          }}
          className="flex-shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-purple-500/10 transition-all active:scale-95"
        >
          <Globe className="w-4 h-4" />
          <span>Trouver des Artistes 🌐</span>
        </button>
      </div>

      {/* Table grid */}
      <div className="flex-1 bg-[#14161B]/50 border border-[#2D3139] rounded-2xl overflow-y-auto p-4 md:p-6 custom-scrollbar relative z-10">
        {(() => {
          let filteredArtists = visibleArtists;

          // Search filter
          if (searchQuery.trim()) {
            const normSearch = normalizeString(searchQuery);
            filteredArtists = filteredArtists.filter(artist => {
              const nameMatch = artist.name ? normalizeString(artist.name).includes(normSearch) : false;
              const roleMatch = artist.roles?.some((r: string) => normalizeString(r).includes(normSearch));
              return nameMatch || roleMatch;
            });
          }

          // Category filter
          if (filterCategoryId) {
            filteredArtists = filteredArtists.filter(a => a.categoryId === filterCategoryId);
          }

          // Custom role filter
          if (filterCustomRole) {
            filteredArtists = filteredArtists.filter(a => a.roles?.includes(filterCustomRole));
          }

          // Bulk helpers (scoped here to access filteredArtists)
          const allSelected = filteredArtists.length > 0 && filteredArtists.every(a => selectedArtistIds.has(a.id));
          const toggleSelectAll = () => {
            const allIds = filteredArtists.map(a => a.id);
            if (allSelected) {
              setSelectedArtistIds(new Set());
            } else {
              setSelectedArtistIds(new Set(allIds));
            }
          };

          if (visibleArtists.length === 0) {
            return (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 space-y-4">
                <Music className="w-16 h-16 text-amber-500/20" />
                <p className="text-center font-medium">Aucun artiste inscrit pour le moment.</p>
                <button onClick={openNew} className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-xl text-xs font-bold transition-all">
                  + Inscrire un artiste
                </button>
              </div>
            );
          }

          if (filteredArtists.length === 0) {
            return (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 space-y-4">
                <Search className="w-16 h-16 text-amber-500/20" />
                <p className="text-center font-medium">Aucun artiste ne correspond à votre recherche.</p>
              </div>
            );
          }

          return (
            <>
              {/* Select-all header */}
              <div className="flex items-center gap-3 mb-4 px-1">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-amber-500 focus:ring-amber-500/20"
                  />
                </label>
                <span className="text-white font-semibold text-sm">
                  {hasSelection ? `${selectedArtistIds.size} / ${filteredArtists.length} sélectionné(s)` : `${filteredArtists.length} artiste(s)`}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredArtists.map((artist) => {
              const isSelected = selectedArtistIds.has(artist.id);
              const artistCategory = categories.find(c => c.id === artist.categoryId);
              // Fetch shifts/performances for this artist
              const artistShifts = shifts.filter(sh => sh.teamId === artist.id);

              return (
                <div
                  key={artist.id}
                  className={`group relative border p-5 rounded-2xl flex flex-col justify-between transition-all duration-300 overflow-hidden ${isSelected ? 'bg-amber-500/5 border-amber-500/50 shadow-lg shadow-amber-500/5' : 'bg-[#0C0E12] border-[#2D3139] hover:border-amber-500/50 hover:shadow-xl hover:shadow-amber-500/5 hover:-translate-y-1'}`}
                >
                  <div
                    className="absolute top-0 left-0 w-1.5 h-full transition-all group-hover:w-2"
                    style={{ backgroundColor: artist.color || '#f59e0b' }}
                  />
                  {/* Checkbox */}
                  <label className="absolute top-3 right-3 z-10 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleArtistSelection(artist.id)}
                      className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-amber-500 focus:ring-amber-500/20"
                    />
                  </label>

                  {/* Top Header Card */}
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        {artist.photo ? (
                          <img 
                            src={artist.photo} 
                            alt={artist.name} 
                            className="w-12 h-12 rounded-xl object-cover border border-[#2D3139]" 
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center w-12 h-12 shrink-0">
                            <UserCircle className="w-7 h-7" />
                          </div>
                        )}
                        {(messages || []).some(m => m.teamId === artist.id && !m.read && m.fromTeam) && (
                          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-orange-500 rounded-full animate-orange-blink border-2 border-[#0C0E12]" title="Nouveau message non lu" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-bold text-white text-lg group-hover:text-amber-400 transition-colors truncate max-w-[150px]">
                          {artist.name}
                          {(artist.memberCount ?? 1) > 1 && (
                            <span className="ml-1.5 text-[10px] font-bold text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded border border-purple-500/20">
                              👥{artist.memberCount}
                            </span>
                          )}
                        </h4>
                        <span className="text-[10px] font-bold text-slate-400 bg-slate-800 px-2 py-0.5 rounded border border-slate-700">
                          {artistCategory?.name || 'Catégorie Artiste'}
                        </span>
                      </div>
                    </div>

                    <div className="flex space-x-1 bg-[#14161B] p-1 rounded-lg border border-[#2D3139]">
                      {artist.emailConfirmed && !isSuperAdmin && artist.id !== currentUser.teamId ? (
                        <button 
                          disabled 
                          className="p-1.5 text-slate-600 cursor-not-allowed rounded"
                          title="Compte vérifié : Modification réservée au Superadmin"
                        >
                          <Lock className="w-4 h-4 text-slate-600" />
                        </button>
                      ) : (
                        <button 
                          onClick={() => openEdit(artist)} 
                          className="p-1.5 text-slate-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      {artist.emailConfirmed && !isSuperAdmin ? (
                        <button 
                          disabled 
                          className="p-1.5 text-slate-600 cursor-not-allowed rounded"
                          title="Compte vérifié : Suppression réservée au Superadmin"
                        >
                          <Trash2 className="w-4 h-4 text-slate-600 opacity-50" />
                        </button>
                      ) : (
                        <button 
                          onClick={() => { if (window.confirm(`Supprimer l'artiste "${artist.name}" ?`)) deleteTeam(artist.id); }} 
                          className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Specialties / Music Style */}
                  <div className="my-4 space-y-1">
                    <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Style / Rôle Scénique :</p>
                    <div className="flex flex-wrap gap-1.5">
                      {artist.roles && artist.roles.length > 0 ? (
                        artist.roles.map((r, i) => (
                          <span key={i} className="text-[10px] font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2.5 py-0.5 rounded-full">
                            {r}
                          </span>
                        ))
                      ) : (
                        <span className="text-[10px] text-slate-500 italic">Non spécifié (ex: DJ Set, Live act)</span>
                      )}
                    </div>
                  </div>

                  {/* Scheduled gigs */}
                  <div className="mb-4 bg-[#14161B]/60 border border-[#2D3139]/80 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-amber-500" />
                        <span>Passages & Scènes ({artistShifts.length})</span>
                      </p>
                      <button 
                        onClick={() => openScheduler(artist)}
                        className="text-[10px] text-amber-400 hover:text-amber-300 font-extrabold flex items-center gap-1 transition-colors"
                      >
                        <Plus className="w-3 h-3" /> Planifier
                      </button>
                    </div>

                    {artistShifts.length === 0 ? (
                      <p className="text-[11px] text-slate-500 italic">Aucun concert ou balance planifiés.</p>
                    ) : (
                      <div className="space-y-1.5 max-h-36 overflow-y-auto custom-scrollbar pr-1">
                        {artistShifts.map((sh) => {
                          const pt = (allPoints || points).find(p => p.id === sh.pointId);
                          const isPerformance = sh.shiftType === 'performance';
                          return (
                            <div 
                              key={sh.id} 
                              className={`text-[11px] px-2.5 py-1.5 rounded-xl flex justify-between items-center transition-all ${isPerformance ? 'bg-amber-500/10 border border-amber-500/20 text-amber-300' : 'bg-orange-500/10 border border-orange-500/20 text-orange-300'}`}
                            >
                              <div className="flex flex-col min-w-0 flex-1">
                                <span className="truncate font-bold text-white" title={sh.description}>
                                  {pt?.name || 'Scène'}
                                </span>
                                <span className="font-mono text-[9px] text-slate-400 mt-0.5">
                                  {new Date(sh.start).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {new Date(sh.end).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 ml-2 shrink-0 opacity-60 hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => openScheduler(artist, sh)}
                                  className="p-1 hover:bg-white/10 rounded-md transition-colors"
                                  title="Modifier ce passage"
                                >
                                  <Edit2 className="w-3.5 h-3.5 text-slate-300 hover:text-amber-400" />
                                </button>
                                <button
                                  onClick={() => { if (window.confirm('Supprimer ce passage ?')) deleteShift(sh.id); }}
                                  className="p-1 hover:bg-red-500/20 rounded-md transition-colors"
                                  title="Supprimer ce passage"
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-slate-300 hover:text-red-400" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Accès Sécurisé Badges */}
                  <div className="pt-3 border-t border-[#2D3139]/50 flex flex-col gap-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-400 font-medium">
                        Badges d'accès : {(artist.memberCount ?? 1) > 1 ? `${artist.memberCount} membres` : 'Application'}
                      </span>
                      {(() => {
                        const memberCount = artist.memberCount ?? 1;
                        if (memberCount === 1) {
                          return artist.teamToken ? (
                            <span className="text-emerald-400 font-bold uppercase tracking-wider text-[10px] bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">Badge Actif</span>
                          ) : null;
                        }
                        const members = team.filter(t => t.parentArtistId === artist.id);
                        const activeCount = members.filter((m: any) => m.teamToken).length;
                        return (
                          <span className="text-emerald-400 font-bold uppercase tracking-wider text-[10px] bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                            {activeCount}/{memberCount} actifs
                          </span>
                        );
                      })()}
                    </div>

                    {(() => {
                      const memberCount = artist.memberCount ?? 1;
                      if (memberCount === 1) {
                        // Solo artist — original behavior
                        return artist.teamToken ? (
                          <div className="flex gap-2">
                            <button onClick={() => showExistingArtistLink(artist)} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/20 rounded-xl transition-all">
                              <Link className="w-3.5 h-3.5" /> Partager Badge
                            </button>
                            <button onClick={() => revokeArtistToken(artist)} className="px-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-xl transition-colors" title="Révoquer l'accès">
                              <Link2Off className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => generateArtistLink(artist)} className="w-full flex items-center justify-center gap-1.5 text-xs font-bold py-2.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 text-amber-300 border border-amber-500/30 rounded-xl transition-all">
                            <Plus className="w-4 h-4 text-amber-400" /> Activer le Badge Digital
                          </button>
                        );
                      }
                      // Multi-member group — show member list
                      const members = team.filter(t => t.parentArtistId === artist.id);
                      return (
                        <div className="space-y-1.5 bg-[#0A0B0E]/60 rounded-xl p-2 border border-[#2D3139]/50">
                          {members.map((member: any, idx: number) => (
                            <div key={member.id} className="flex items-center justify-between gap-2">
                              <span className="text-[10px] text-slate-300 truncate flex-1">Membre {idx + 1} {member.teamToken ? '✅' : '⬜'}</span>
                              {member.teamToken ? (
                                <div className="flex gap-1">
                                  <button onClick={() => showExistingArtistLink(member)} className="text-[9px] font-bold px-2 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/20 rounded-lg transition-all" title="Partager">
                                    <Link className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => revokeArtistToken(member)} className="text-[9px] font-bold px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg transition-all" title="Révoquer">
                                    <Link2Off className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <button onClick={() => generateArtistLink(member)} className="text-[9px] font-bold px-2 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20 rounded-lg transition-all">
                                  <Plus className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        );
      })()}

      {/* Bulk Action Bar */}
      {hasSelection && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1A1D23]/95 backdrop-blur-sm border-t border-amber-500/30 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
            <span className="text-white font-semibold text-sm mr-2">
              {selectedArtistIds.size} sélectionné{selectedArtistIds.size > 1 ? 's' : ''}
            </span>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg text-xs font-medium transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Supprimer
            </button>
            <button
              onClick={handleBulkMessage}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 text-indigo-400 rounded-lg text-xs font-medium transition-colors"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Message
            </button>
            <button
              onClick={() => {
                setBulkCategoryId(artistCategoryIds[0] || '');
                setShowBulkCategoryModal(true);
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-lg text-xs font-medium transition-colors"
            >
              <FolderKanban className="w-3.5 h-3.5" />
              Changer catégorie
            </button>
            <button
              onClick={clearSelection}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-slate-300 rounded-lg text-xs font-medium transition-colors ml-auto"
            >
              <X className="w-3.5 h-3.5" />
              Désélectionner
            </button>
          </div>
        </div>
      )}

      {/* Bulk Category Reassign Modal */}
      {showBulkCategoryModal && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-white font-semibold mb-1">Changer la catégorie</h3>
            <p className="text-slate-400 text-sm mb-4">
              {selectedArtistIds.size} artiste(s) seront déplacés.
            </p>
            <select
              value={bulkCategoryId}
              onChange={e => setBulkCategoryId(e.target.value)}
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 transition-all appearance-none mb-4"
            >
              {artistCategories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowBulkCategoryModal(false)}
                className="flex-1 px-4 py-2.5 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleBulkReassignCategory}
                disabled={!bulkCategoryId}
                className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Déplacer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

      {/* ── Add / Edit Artist Modal ────────────────────────────────────── */}
      <ConfigModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={formData.id ? "Modifier l'Artiste" : "Inscrire un nouvel Artiste"}
        icon={<Music className="w-5 h-5 text-amber-400" />}
        isDirty={isDirty}
      >
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
          {/* Main Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Nom de Scène / Groupe</label>
              <input
                type="text"
                autoFocus
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder-slate-600 font-semibold"
                placeholder="Ex: DJ Snake"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Catégorie Scénique</label>
              {effectiveCategoryId ? (
                <div className="w-full bg-[#1A1D23] border border-[#2D3139] rounded-xl px-4 py-3 text-slate-300 font-medium">
                  {categories.find(c => c.id === effectiveCategoryId)?.name || 'Votre Catégorie'}
                </div>
              ) : (
                <select
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all"
                  value={formData.categoryId}
                  onChange={(e) => setFormData(prev => ({ ...prev, categoryId: e.target.value }))}
                >
                  {artistCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                  {artistCategories.length === 0 && (
                    <option value="">Aucune catégorie d'artistes trouvée</option>
                  )}
                </select>
              )}
            </div>
          </div>

          {/* Contact coordinates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Email de Coordination</label>
              <input
                type="email"
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder-slate-600"
                placeholder="coordination@artiste.com"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Téléphone</label>
              <input
                type="text"
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder-slate-600"
                placeholder="+33 6 12 34 56 78"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
          </div>

          {/* Member count for groups */}
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">👥 Nombre de membres (groupe)</label>
            <input
              type="number"
              min={1}
              max={20}
              className="w-24 bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder-slate-600 font-semibold text-center"
              value={formData.memberCount ?? 1}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                setFormData(prev => ({ ...prev, memberCount: Math.max(1, Math.min(20, val)) }));
              }}
            />
            <p className="text-[10px] text-slate-500 mt-1">Pour un groupe, chaque membre aura son propre badge d'accès avec QR code, quotas et messages individuels. Planning partagé.</p>
          </div>

          {/* Assigned Team Member */}
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">👤 Membre Team en charge</label>
            <select
              value={formData.assignedTeamId || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, assignedTeamId: e.target.value || undefined }))}
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all appearance-none"
            >
              <option value="">-- Aucun --</option>
              {team.filter(s => !s.isArtist && !s.parentArtistId).sort((a, b) => a.name.localeCompare(b.name)).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-slate-500 mt-1">Le membre team qui sera le contact privilégié de l'artiste pour la messagerie.</p>
          </div>

          {/* Musical Genres / Roles */}
          <div>
            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Genres Musicaux / Spécificités (ex: DJ Set, Live act, Reggae)</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder-slate-600"
                placeholder="Ex: DJ Set, Melodic Techno"
                value={customRoleInput}
                onChange={(e) => setCustomRoleInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customRoleInput.trim()) {
                    e.preventDefault();
                    if (!formData.roles.includes(customRoleInput.trim())) {
                      setFormData(prev => ({ ...prev, roles: [...prev.roles, customRoleInput.trim()] }));
                    }
                    setCustomRoleInput('');
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  if (customRoleInput.trim()) {
                    if (!formData.roles.includes(customRoleInput.trim())) {
                      setFormData(prev => ({ ...prev, roles: [...prev.roles, customRoleInput.trim()] }));
                    }
                    setCustomRoleInput('');
                  }
                }}
                className="px-4 py-2 bg-[#2D3139] text-white hover:bg-slate-700 rounded-xl text-sm font-semibold transition-colors"
              >
                + Ajouter
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {formData.roles.map((r, idx) => (
                <span key={idx} className="flex items-center gap-1.5 text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 px-3 py-1 rounded-full">
                  {r}
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, roles: prev.roles.filter(role => role !== r) }))}
                    className="text-amber-500 hover:text-white rounded-full transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Photo & Design Accent Color */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Logo / Photo de l'Artiste</label>
              <div className="flex items-center gap-3">
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handlePhotoUpload} 
                  accept="image/*" 
                  className="hidden" 
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2.5 bg-[#1A1D23] hover:bg-[#22262E] text-slate-300 border border-[#2D3139] rounded-xl text-xs font-bold transition-all"
                >
                  Téléverser une image
                </button>
                {formData.photo && (
                  <img src={formData.photo} alt="Preview" className="w-10 h-10 rounded-lg object-cover border border-[#2D3139]" />
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Couleur Distinctive</label>
              <div className="flex gap-2 items-center flex-wrap">
                {ARTIST_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`w-6 h-6 rounded-full border-2 transition-all ${formData.color === c ? 'border-white scale-110 shadow-lg' : 'border-transparent'}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setFormData(prev => ({ ...prev, color: c }))}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Buttons links */}
          <div className="pt-3 border-t border-[#2D3139]/50">
            <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Documents Logistiques / Rider Technique</h5>
            
            {formData.customButtons?.map((btn, idx) => (
              <div key={idx} className="flex gap-2 items-center mb-3 bg-[#0A0B0E] p-3 rounded-xl border border-[#2D3139]/70 relative">
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-1.5 text-xs text-white"
                    placeholder="Titre (ex: Rider technique)"
                    value={btn.label}
                    onChange={(e) => {
                      const newBtns = [...(formData.customButtons || [])];
                      newBtns[idx].label = e.target.value;
                      setFormData(prev => ({ ...prev, customButtons: newBtns }));
                    }}
                  />
                  <input
                    type="text"
                    className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-1.5 text-xs text-white font-mono"
                    placeholder="URL (ex: https://site.com/rider.pdf)"
                    value={btn.url}
                    onChange={(e) => {
                      const newBtns = [...(formData.customButtons || [])];
                      newBtns[idx].url = e.target.value;
                      setFormData(prev => ({ ...prev, customButtons: newBtns }));
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ 
                      ...prev, 
                      customButtons: prev.customButtons?.filter((_, i) => i !== idx) 
                    }));
                  }}
                  className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}

            {(formData.customButtons || []).length < 3 && (
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    customButtons: [...(prev.customButtons || []), { label: '', url: '', color: 'amber' }]
                  }));
                }}
                className="w-full py-2 bg-[#1A1D23] hover:bg-[#22262E] text-amber-400 border border-dashed border-[#2D3139] hover:border-amber-500/50 rounded-xl text-xs font-bold transition-all"
              >
                + Attacher un lien de Rider / Logistique
              </button>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex gap-3 pt-4 border-t border-[#2D3139]/50">
            <button 
              type="button"
              onClick={() => setModalOpen(false)} 
              className="flex-1 px-4 py-3 border border-[#2D3139] hover:bg-[#1A1D23] text-white rounded-xl transition-all flex items-center justify-center gap-2 font-medium text-sm"
            >
              <X className="w-4 h-4" /> Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!formData.name}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:opacity-50 text-white rounded-xl transition-all shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 font-bold text-sm"
            >
              {formData.id ? <Edit2 className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
              {formData.id ? 'Mettre à jour' : 'Inscrire'}
            </button>
          </div>
        </div>
      </ConfigModal>

      {/* ── Live Performance Scheduler Modal ─────────────────────────────── */}
      <ConfigModal
        isOpen={schedModalOpen}
        onClose={() => setSchedModalOpen(false)}
        title={editingShiftId ? `Modifier le passage de : ${selectedArtistForSched?.name || ''}` : `Planifier un passage pour : ${selectedArtistForSched?.name || ''}`}
        icon={<Calendar className="w-5 h-5 text-amber-400" />}
        isDirty={true}
      >
        <div className="p-5 space-y-4">
          <div className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-3.5 flex gap-4">
            <div className="flex-1">
              <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Type de Passage</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={`py-2 rounded-xl text-xs font-bold border transition-all ${schedForm.shiftType === 'performance' ? 'bg-amber-500/10 text-amber-400 border-amber-500/50' : 'bg-transparent text-slate-400 border-[#2D3139] hover:border-slate-600'}`}
                  onClick={() => setSchedForm(prev => ({ ...prev, shiftType: 'performance' }))}
                >
                  🎭 Concert / Live
                </button>
                <button
                  type="button"
                  className={`py-2 rounded-xl text-xs font-bold border transition-all ${schedForm.shiftType === 'soundcheck' ? 'bg-orange-500/10 text-orange-400 border-orange-500/50' : 'bg-transparent text-slate-400 border-[#2D3139] hover:border-slate-600'}`}
                  onClick={() => setSchedForm(prev => ({ ...prev, shiftType: 'soundcheck' }))}
                >
                  ⚡ Soundcheck / Balance
                </button>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Scène / Lieu</label>
            <select
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all font-semibold"
              value={schedForm.pointId}
              onChange={(e) => setSchedForm(prev => ({ ...prev, pointId: e.target.value }))}
            >
              {(allPoints || points).map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.isStage ? '🎭 (Scène / Stage)' : ''}
                </option>
              ))}
              {(allPoints || points).length === 0 && (
                <option value="">Aucune scène configurée</option>
              )}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Début du set</label>
              <input
                type="datetime-local"
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all font-mono"
                value={schedForm.start}
                onChange={(e) => setSchedForm(prev => ({ ...prev, start: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Fin du set</label>
              <input
                type="datetime-local"
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all font-mono"
                value={schedForm.end}
                onChange={(e) => setSchedForm(prev => ({ ...prev, end: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">Description du passage (Optionnel)</label>
            <input
              type="text"
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/50 transition-all placeholder-slate-600"
              placeholder="Ex: Mainstage Live Set, DJ Set Warmup"
              value={schedForm.description}
              onChange={(e) => setSchedForm(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div className="flex gap-3 pt-4 border-t border-[#2D3139]/50">
            <button
              type="button"
              onClick={() => setSchedModalOpen(false)}
              className="flex-1 px-4 py-3 border border-[#2D3139] hover:bg-[#1A1D23] text-white rounded-xl transition-all flex items-center justify-center gap-2 font-medium text-sm"
            >
              <X className="w-4 h-4" /> Annuler
            </button>
            <button
              type="button"
              onClick={saveSchedule}
              disabled={!schedForm.pointId}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-xl transition-all shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 font-bold text-sm"
            >
              <Calendar className="w-4 h-4" /> {editingShiftId ? 'Mettre à jour le Passage' : 'Planifier le Passage'}
            </button>
          </div>
        </div>
      </ConfigModal>

      {/* ── Link Modal ────────────────────────────────────────────────── */}
      {linkModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[110] animate-in fade-in">
          <div className="bg-[#14161B] border border-[#2D3139] p-8 rounded-2xl w-full max-w-md shadow-2xl relative">
            <div className="absolute top-[-10%] right-[-5%] w-48 h-48 bg-amber-500/10 rounded-full blur-[60px] pointer-events-none" />
            <div className="w-12 h-12 bg-amber-500/20 text-amber-400 rounded-2xl flex items-center justify-center mb-6 border border-amber-500/30">
              <Shield className="w-6 h-6 animate-pulse" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Badge d'Accès Artiste VIP</h3>
            <p className="text-sm text-slate-400 mb-6">Artiste : <span className="text-white font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2 py-1 rounded ml-1">{linkModal.artistName}</span></p>
            
            <div className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-4 mb-6 flex items-center gap-3 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" />
              <span className="text-xs font-mono text-slate-300 flex-1 truncate select-all">{linkModal.link}</span>
              <button 
                onClick={() => copyText(linkModal.link, 'artist-link')} 
                className="flex-shrink-0 p-2 bg-[#1A1D23] hover:bg-amber-500 hover:text-white rounded-lg text-slate-400 transition-all"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {copiedId === 'artist-link' && <p className="text-xs text-emerald-400 mb-4">✓ Lien copié avec succès !</p>}
            
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-xs text-amber-400 leading-relaxed">Ce lien donne un accès direct au badge digital de l'artiste, à son planning et à ses quotas catering. Transmettez-le de manière confidentielle.</p>
            </div>
            <div className="space-y-3">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Salut ! Voici ton lien personnel pour ton Badge Digital VIP Artiste 🎨 pour le festival. Il contient ton QR Code d'accès, tes quotas de catering et ton planning de passage :\n\n${linkModal.link}\n\nA très vite !`)}`}
                target="_blank" rel="noopener noreferrer"
                className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl py-3 flex items-center justify-center space-x-2 transition-all font-bold text-sm"
              >
                <span>Partager sur WhatsApp</span>
              </a>
              <button 
                onClick={() => setLinkModal(null)} 
                className="w-full bg-transparent hover:bg-[#1A1D23] border border-[#2D3139] text-white transition-colors py-3 rounded-xl text-sm font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Discovery Modal ────────────────────────────────────────────── */}
      {findArtistsOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[120] animate-in fade-in">
          <div className="bg-[#14161B] border border-[#2D3139] rounded-3xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95">
            {/* Modal Header */}
            <div className="p-6 border-b border-[#2D3139] flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Globe className="text-amber-400 w-5 h-5 animate-pulse" /> Trouver des Artistes Globaux 🌐
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Recherchez et recrutez des artistes de la base de données globale de Bip Teams pour votre line-up.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isSuperAdmin && (
                  <>
                    <input
                      type="file"
                      ref={fileImportRef}
                      onChange={handleImportCsv}
                      accept=".csv"
                      className="hidden"
                    />
                    <button
                      onClick={() => fileImportRef.current?.click()}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
                      title="SuperAdmin ONLY: Importer des artistes"
                    >
                      <FileText className="w-3.5 h-3.5" /> Importer CSV
                    </button>
                  </>
                )}
                <button 
                  onClick={() => setFindArtistsOpen(false)}
                  className="p-2 bg-[#1A1D23] hover:bg-[#22262E] text-slate-400 hover:text-white rounded-xl border border-[#2D3139] transition-colors"
                >✕</button>
              </div>
            </div>

            {/* Filters */}
            <div className="p-6 bg-[#0A0B0E]/60 border-b border-[#2D3139] grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Nom ou Bio</label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                  <input 
                    type="text" 
                    value={publicSearch} 
                    onChange={e => setPublicSearch(e.target.value)} 
                    placeholder="Nom de l'artiste..." 
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl pl-9 pr-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Genre Principal</label>
                <select 
                  value={publicGenre} 
                  onChange={e => setPublicGenre(e.target.value)} 
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="">Tous genres</option>
                  {Array.from(new Set(publicArtists.map(a => a.genres).filter(Boolean))).map((genre: any) => (
                    <option key={genre} value={genre}>{genre}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results Grid */}
            <div className="flex-1 overflow-y-auto p-6 bg-[#0C0E12]/80 custom-scrollbar">
              {loadingPublic ? (
                <div className="flex flex-col items-center justify-center h-48 space-y-3">
                  <div className="w-8 h-8 border-2 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
                  <span className="text-xs text-slate-400">Chargement de la base d'artistes...</span>
                </div>
              ) : (() => {
                const filtered = publicArtists.filter(artist => {
                  const normSearch = normalizeString(publicSearch);
                  const nameMatch = artist.name ? normalizeString(artist.name).includes(normSearch) : false;
                  const bioMatch = artist.bio ? normalizeString(artist.bio).includes(normSearch) : false;
                  const genreMatch = !publicGenre || artist.genres === publicGenre;
                  return (nameMatch || bioMatch) && genreMatch;
                });

                if (filtered.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                      <Music className="w-12 h-12 text-amber-500/10 mb-2" />
                      <p className="text-xs">Aucun artiste ne correspond à ces critères dans la base globale.</p>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filtered.map((artist) => {
                      const isAlreadyInLineup = team.some(s => 
                        s.isArtist && 
                        s.orgId === currentOrgId && 
                        s.name.toLowerCase() === artist.name.toLowerCase()
                      );

                      return (
                        <div 
                          key={artist.id} 
                          className="bg-[#14161B] border border-[#2D3139] hover:border-amber-500/30 p-5 rounded-2xl flex flex-col justify-between gap-4 transition-all duration-200"
                        >
                          <div className="flex items-start gap-4">
                            {artist.image_url ? (
                              <img 
                                src={artist.image_url} 
                                alt={artist.name} 
                                className="w-16 h-16 rounded-xl object-cover border border-[#2D3139] flex-shrink-0" 
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center flex-shrink-0">
                                <UserCircle className="w-10 h-10" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <h4 className="font-extrabold text-white text-base truncate">{artist.name}</h4>
                                {isSuperAdmin && (
                                  <button
                                    onClick={() => handleDeletePublicArtist(artist.id, artist.name)}
                                    className="p-1 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                    title="SuperAdmin ONLY: Supprimer de la base globale"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {artist.genres && (
                                  <span className="text-[9px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded">
                                    {artist.genres}
                                  </span>
                                )}
                                {artist.category && (
                                  <span className="text-[9px] font-bold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-2 py-0.5 rounded">
                                    {artist.category}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-400 mt-2 line-clamp-2 leading-relaxed" title={artist.bio}>
                                {artist.bio || "Aucune description renseignée."}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#2D3139]/50">
                            {/* Social Links */}
                            <div className="flex items-center gap-1.5">
                              {artist.spotify && (
                                <a 
                                  href={artist.spotify} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="p-1.5 bg-[#1C2026] hover:bg-[#1DB954]/10 text-slate-400 hover:text-[#1DB954] rounded-lg border border-[#2D3139] transition-all"
                                  title="Spotify"
                                >
                                  <ExternalLink className="w-3.5 h-3.5 animate-pulse" />
                                </a>
                              )}
                              {artist.soundcloud && (
                                <a 
                                  href={artist.soundcloud} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="p-1.5 bg-[#1C2026] hover:bg-[#FF5500]/10 text-slate-400 hover:text-[#FF5500] rounded-lg border border-[#2D3139] transition-all"
                                  title="SoundCloud"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                              {artist.instagram && (
                                <a 
                                  href={artist.instagram} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="p-1.5 bg-[#1C2026] hover:bg-[#E1306C]/10 text-slate-400 hover:text-[#E1306C] rounded-lg border border-[#2D3139] transition-all"
                                  title="Instagram"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              )}
                              {artist.email && (
                                <a 
                                  href={`mailto:${artist.email}`} 
                                  className="p-1.5 bg-[#1C2026] hover:bg-blue-500/10 text-slate-400 hover:text-blue-400 rounded-lg border border-[#2D3139] transition-all"
                                  title={`Email: ${artist.email}`}
                                >
                                  <Mail className="w-3.5 h-3.5" />
                                </a>
                              )}
                            </div>

                            {/* Recruit Button */}
                            {isAlreadyInLineup ? (
                              <span className="text-[11px] font-bold text-slate-500 bg-[#1A1D23] px-3 py-1.5 rounded-xl border border-[#2D3139]">
                                ✓ Déjà au Line-up
                              </span>
                            ) : (
                              <button
                                onClick={() => handleAddPublicArtistToLineup(artist)}
                                className="px-4 py-1.5 bg-amber-500 hover:bg-amber-400 text-white font-bold text-xs rounded-xl shadow-lg shadow-amber-500/10 transition-all active:scale-95"
                              >
                                + Recruter
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
